import React, { useState, useMemo } from "react";
import * as XLSX from "xlsx";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TablePagination,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Divider,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import RemoveIcon from "@mui/icons-material/Remove";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import ShowChartIcon from "@mui/icons-material/ShowChart";

// ─── Shared constants & formatters ───────────────────────────────────────────
export const QUARTERS = ["Q1", "Q2", "Q3", "Q4"];

export const getQuarter = (dateStr) => {
  const m = new Date(dateStr).getMonth();
  if (m >= 3 && m <= 5) return "Q1";
  if (m >= 6 && m <= 8) return "Q2";
  if (m >= 9 && m <= 11) return "Q3";
  return "Q4";
};

export const getFinancialYear = (dateStr) => {
  const d = new Date(dateStr);
  return d.getMonth() < 3 ? d.getFullYear() : d.getFullYear() + 1;
};

export const fmt = (v, pct = false) => {
  if (typeof v !== "number" || isNaN(v)) return pct ? "0.00%" : "0.00";
  const s = new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v);
  return pct ? `${s}%` : s;
};

export const fmtCur = (v) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(v || 0);

export const fmtShort = (v) => {
  const n = v || 0;
  if (Math.abs(n) >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (Math.abs(n) >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (Math.abs(n) >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
  return `₹${n.toFixed(0)}`;
};

const deltaColor = (v) => (v > 0 ? "#16a34a" : v < 0 ? "#dc2626" : "#6b7280");
const deltaIcon = (v) =>
  v > 0 ? (
    <ArrowUpwardIcon sx={{ fontSize: 11 }} />
  ) : v < 0 ? (
    <ArrowDownwardIcon sx={{ fontSize: 11 }} />
  ) : (
    <RemoveIcon sx={{ fontSize: 11 }} />
  );

export const DeltaBadge = ({ value }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.3,
      color: deltaColor(value),
      fontWeight: 700,
      fontSize: "0.75rem",
    }}
  >
    {deltaIcon(value)}
    {Math.abs(value).toFixed(1)}%
  </Box>
);

export const MarginChip = ({ value }) => (
  <Chip
    size="small"
    icon={
      value >= 0 ? (
        <TrendingUpIcon fontSize="small" />
      ) : (
        <TrendingDownIcon fontSize="small" />
      )
    }
    label={fmt(value, true)}
    sx={{
      bgcolor: value >= 0 ? "#f0fdf4" : "#fff1f2",
      color: value >= 0 ? "#15803d" : "#dc2626",
      fontWeight: 700,
      fontSize: "0.74rem",
      border: `1px solid ${value >= 0 ? "#bbf7d0" : "#fecaca"}`,
    }}
  />
);

export const SummaryStrip = ({ totals, extra = [] }) => {
  const items = [
    { label: "Total Sales", value: fmtShort(totals.totalSp), bg: "#eff6ff", color: "#1d4ed8" },
    { label: "Total Cost", value: fmtShort(totals.totalPp), bg: "#faf5ff", color: "#7c3aed" },
    {
      label: "Total Margin",
      value: fmtShort(totals.margin),
      bg: totals.margin >= 0 ? "#f0fdf4" : "#fff1f2",
      color: totals.margin >= 0 ? "#15803d" : "#dc2626",
    },
    {
      label: "Margin %",
      value: fmt(totals.totalSp > 0 ? (totals.margin / totals.totalSp) * 100 : 0, true),
      bg: "#f0fdfa",
      color: "#0f766e",
    },
    ...extra,
  ];
  return (
    <Box sx={{ display: "flex", gap: 2, px: 3, pb: 2, flexWrap: "wrap" }}>
      {items.map((s) => (
        <Box key={s.label} sx={{ bgcolor: s.bg, borderRadius: 2, px: 2, py: 1, minWidth: 110 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {s.label}
          </Typography>
          <Typography fontWeight={800} sx={{ color: s.color, fontSize: "1rem" }}>
            {s.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

// ─── Period comparison table (QoQ for a single entity) ───────────────────────
export const PeriodCompareTable = ({ records, groupBy = "quarter" }) => {
  const byQ = useMemo(() => {
    const g = {};
    QUARTERS.forEach((q) => {
      g[q] = { totalSp: 0, totalPp: 0, margin: 0, count: 0 };
    });
    records.forEach((r) => {
      const q = r.quarter;
      if (!g[q]) return;
      g[q].totalSp += r.totalSp;
      g[q].totalPp += r.totalPp;
      g[q].margin += r.margin;
      g[q].count++;
    });
    QUARTERS.forEach((q) => {
      g[q].marginPct = g[q].totalPp > 0 ? (g[q].margin / g[q].totalPp) * 100 : 0;
    });
    return g;
  }, [records]);

  const th = { fontWeight: 700, bgcolor: "#f8fafc", fontSize: "0.78rem", textAlign: "center" };

  const deltaVsPrev = (q, key) => {
    const idx = QUARTERS.indexOf(q);
    if (idx === 0) return null;
    const prev = byQ[QUARTERS[idx - 1]][key];
    const cur = byQ[q][key];
    return prev !== 0 ? ((cur - prev) / Math.abs(prev)) * 100 : null;
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Typography
        variant="subtitle2"
        fontWeight={700}
        sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 0.5 }}
      >
        <ShowChartIcon fontSize="small" sx={{ color: "#1d4ed8" }} /> Quarter-on-Quarter Breakdown
      </Typography>
      <TableContainer sx={{ borderRadius: 2, border: "1px solid #e2e8f0" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...th, textAlign: "left" }}>Metric</TableCell>
              {QUARTERS.map((q) => (
                <TableCell key={q} sx={th}>
                  {q}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {[
              { label: "Sales", key: "totalSp", render: fmtShort },
              { label: "Cost", key: "totalPp", render: fmtShort },
              { label: "Margin", key: "margin", render: fmtShort },
              { label: "Margin %", key: "marginPct", render: (v) => fmt(v, true) },
              { label: "Orders", key: "count", render: (v) => v },
            ].map((row) => (
              <TableRow key={row.label} hover>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem" }}>{row.label}</TableCell>
                {QUARTERS.map((q) => {
                  const val = byQ[q][row.key];
                  const d = deltaVsPrev(q, row.key);
                  const active = byQ[q].count > 0;
                  return (
                    <TableCell
                      key={q}
                      align="center"
                      sx={{ fontSize: "0.8rem", color: active ? "inherit" : "#cbd5e1" }}
                    >
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.2 }}>
                        <span style={{ fontWeight: active ? 700 : 400 }}>
                          {active ? row.render(val) : "—"}
                        </span>
                        {d !== null && active && <DeltaBadge value={d} />}
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// ─── Rich costsheet drill-down dialog ────────────────────────────────────────
// records: array of { salesOrderNo, clientName, date, quarter, costsheetfor,
//                      totalSp, totalPp, margin, marginPercentage }
// details: optional content shown between the summary strip and the tabs (e.g. PartyDetailsPanel) 
const CostsheetDrillDialog = ({ open, onClose, title, subtitle, records, showQoQ = true, details }) => {
  const [tab, setTab] = useState(0); // 0 = Costsheets, 1 = QoQ
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rpp, setRpp] = useState(15);

  const totals = useMemo(
    () =>
      records.reduce(
        (a, r) => {
          a.totalSp += r.totalSp;
          a.totalPp += r.totalPp;
          a.margin += r.margin;
          return a;
        },
        { totalSp: 0, totalPp: 0, margin: 0 }
      ),
    [records]
  );

  const filtered = useMemo(
    () =>
      !search
        ? records
        : records.filter(
            (r) =>
              (r.clientName || "").toLowerCase().includes(search.toLowerCase()) ||
              (r.salesOrderNo || "").toLowerCase().includes(search.toLowerCase()) ||
              (r.costsheetfor || "").toLowerCase().includes(search.toLowerCase())
          ),
    [records, search]
  );

  const exportXlsx = () => {
    const rows = filtered.map((r) => ({
      "Sales Order No": r.salesOrderNo || "",
      Client: r.clientName,
      Date: r.date,
      Quarter: r.quarter,
      "Cost Sheet For": r.costsheetfor || "",
      "Sales (INR)": r.totalSp,
      "Cost (INR)": r.totalPp,
      "Margin (INR)": r.margin,
      "Margin %": fmt(r.marginPercentage, true),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = Object.keys(rows[0] || {}).map((k) => ({ wch: Math.max(k.length, 14) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Costsheets");
    XLSX.writeFile(wb, `${(title || "export").replace(/\s+/g, "_")}.xlsx`);
  };

  const th = { fontWeight: 700, bgcolor: "#f8fafc", fontSize: "0.79rem", whiteSpace: "nowrap" };
  const paginated = filtered.slice(page * rpp, page * rpp + rpp);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, maxHeight: "90vh" } }}
    >
      <DialogTitle
        sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", fontWeight: 800, pb: 1 }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <OpenInNewIcon fontSize="small" sx={{ color: "#1d4ed8" }} />
          <Box>
            <Typography fontWeight={800} variant="h6" sx={{ lineHeight: 1.2 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <SummaryStrip
        totals={totals}
        extra={[{ label: "Cost Sheets", value: records.length, bg: "#f1f5f9", color: "#334155" }]}
      />

      {details && <Box sx={{ px: 3, pb: 2 }}>{details}</Box>}

      {showQoQ && (
        <Box sx={{ px: 3, mb: 1 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ minHeight: 36 }}>
            <Tab label="Cost Sheets" sx={{ textTransform: "none", fontWeight: 700, fontSize: "0.82rem", minHeight: 36 }} />
            <Tab label="QoQ Comparison" sx={{ textTransform: "none", fontWeight: 700, fontSize: "0.82rem", minHeight: 36 }} />
          </Tabs>
          <Divider />
        </Box>
      )}

      <DialogContent sx={{ p: 0, px: showQoQ && tab === 1 ? 3 : 0, pb: 1 }}>
        {!showQoQ || tab === 0 ? (
          <>
            <Box
              sx={{
                px: 3,
                py: 1.5,
                display: "flex",
                gap: 2,
                alignItems: "center",
                borderBottom: "1px solid #f1f5f9",
              }}
            >
              <TextField
                size="small"
                placeholder="Search client, order no, type…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 300 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
                {filtered.length} of {records.length} records
              </Typography>
            </Box>
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ "& th": th }}>
                    <TableCell>#</TableCell>
                    <TableCell>Sales Order No</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Qtr</TableCell>
                    <TableCell>Cost Sheet For</TableCell>
                    <TableCell align="right">Sales</TableCell>
                    <TableCell align="right">Cost</TableCell>
                    <TableCell align="right">Margin</TableCell>
                    <TableCell align="right">Margin %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.map((r, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ color: "#94a3b8", fontSize: "0.78rem" }}>
                        {page * rpp + i + 1}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.82rem", fontWeight: 600 }}>
                        {r.salesOrderNo || "—"}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.82rem", fontWeight: 600 }}>{r.clientName}</TableCell>
                      <TableCell sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
                        {r.date?.split?.("T")[0] || r.date}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={r.quarter} sx={{ height: 18, fontSize: "0.68rem" }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.82rem" }}>{r.costsheetfor || "—"}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {fmtShort(r.totalSp)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {fmtShort(r.totalPp)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 700, color: r.margin >= 0 ? "#15803d" : "#dc2626" }}
                      >
                        {fmtShort(r.margin)}
                      </TableCell>
                      <TableCell align="right">
                        <MarginChip value={r.marginPercentage} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rpp}
              onRowsPerPageChange={(e) => {
                setRpp(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[15, 25, 50, 100]}
            />
          </>
        ) : (
          <PeriodCompareTable records={records} />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e2e8f0" }}>
        <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={exportXlsx}>
          Export to Excel
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CostsheetDrillDialog;